import { OnboardingWebsiteDetails } from "../types";
import { ChatMessage } from "@/lib/ai/types";

export function getWebsiteDetailsAsQualifier(
  websiteDetails: OnboardingWebsiteDetails
) {
  const metadata = [
    {
      name: "Website Name",
      value: websiteDetails.websiteName
    },
    {
      name: "Website Description",
      value: websiteDetails.websiteDescription
    },
    {
      name: "Website Category",
      value: websiteDetails.websiteCategory
        .map(category => category.label)
        .join(", ")
    },
    {
      name: "Website Goals",
      value: websiteDetails.websiteGoals.map(goal => goal.label).join(", ")
    },
    {
      name: "Website Writing Style",
      value: websiteDetails.websiteWritingStyle
        .map(personality => personality.label)
        .join(", ")
    }
  ];

  return metadata.reduce((acc, curr) => {
    if (!curr.value) return acc;
    return `${acc}\n${curr.name}: ${curr.value}`;
  }, "");
}

export function getTaglinePrompt(
  websiteDetails: OnboardingWebsiteDetails
): ChatMessage[] {
  const details = getWebsiteDetailsAsQualifier(websiteDetails);
  return [
    {
      role: "system",
      content: `
          ## Instructions:
          You are an imaginative and adaptable copywriter. Your task is to write a unique, compelling tagline that reflects the tone, personality, and purpose of a specific website.
          This tagline will serve as a sample sentence that defines the language, tone, and style of the website. You will be given structured information about the website, such as its name, category, description, goals, and personality traits.
          Your output must be relevant to the site's identity and stand out from generic phrases.

          ## Guidelines:
          - The tagline must include the website's name somewhere in the sentence.
          - It must reflect the website's category, goals, and personality.
          - It should be between 15 and 20 words.
          - It should not begin with the website name followed by a colon.
          - Avoid clichés and overly generic wording.
          - Match the tone to the personality traits (e.g., bold and playful, sleek and serious, warm and professional).
          - Do not use personality traits or goals provided in the input in the tagline.
          - Sentence case only.
          - Language: English.

          ## Example Input:
          - Website Name: "Tech Innovations"
          - Website Description: "A platform for the latest tech news and reviews."
          - Website Category: "Technology"
          - Website Goals: "Inform, Engage, Inspire"
          - Website Writing Style: "Innovative, Friendly, Professional"

          ## Response format:
          - The response should be in JSON format.
          - The response should include the tagline in a field called "tagline".

          ## Example Output:
          {
            "tagline": "Tech Innovations is your go-to guide for insightful, friendly tech news and cutting-edge reviews."
          }

        `
    },
    {
      role: "user",
      content: details
    }
  ];
}
